import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../redux/network/https";
import { endPoints, queryKeys } from "../../redux/network/constant";

const invalidateAttachmentRelatedQueries = ({
  queryClient,
  projectId,
  objectType,
  fileId,
}: {
  queryClient: ReturnType<typeof useQueryClient>;
  projectId: number;
  objectType?: string;
  fileId?: number;
}) => {
  queryClient.invalidateQueries({
    queryKey: ["projects", "file-attachments", projectId],
  });

  if (fileId) {
    queryClient.invalidateQueries({
      queryKey: queryKeys.ProjectFileWorkflowRefs(projectId, fileId),
    });
  }

  if (objectType === "TASK_COMMENT") {
    queryClient.invalidateQueries({
      queryKey: ["projects", "tasks", "comments", projectId],
      exact: false,
    });
  }
};

// ── Types ────────────────────────────────────────────────────────────────────

export interface FileAttachment {
  id: number;
  file: {
    id: number;
    original_name: string;
    stored_name: string;
    extension: string;
    category: string;
    size_bytes: number;
    upload_status: string;
    is_previewable: boolean;
    folder_path: number | null;
  };
  object_type: string;
  object_id: number;
  object_title: string;
  role: string;
  status: string;
  requirement_key: string;
  note: string;
  review_note: string;
  sort_order: number;
  created_by: number;
  created_by_name: string;
  submitted_by: number | null;
  submitted_by_name: string;
  reviewed_by: number | null;
  reviewed_by_name: string;
  created_at: string;
  submitted_at: string | null;
  reviewed_at: string | null;
  updated_at: string;
}

export interface FileAttachmentListResponse {
  items: FileAttachment[];
  count: number;
  permissions?: {
    can_attach: boolean;
    can_detach: boolean;
  };
}

export interface FileAttachmentFilters {
  object_type?: string;
  object_id?: number;
  file_id?: number;
  status?: string;
  role?: string;
  include_unavailable?: boolean;
}

export interface CreateAttachmentPayload {
  file_id: number;
  object_type: string;
  object_id: number;
  role?: string;
  requirement_key?: string;
  note?: string;
  sort_order?: number;
}

export interface UploadAndAttachPayload {
  files: { original_name: string; content_type: string; size_bytes: number }[];
  folder_id?: number | null;
  attach_to: {
    object_type: string;
    object_id: number;
    role?: string;
    requirement_key?: string;
    note?: string;
  };
}

// ── File Attachments List ─────────────────────────────────────────────────────

const fetchFileAttachments = async ({ queryKey }: { queryKey: readonly unknown[] }) => {
  const [, , projectId, filters] = queryKey as [
    string, string, number, FileAttachmentFilters | undefined,
  ];

  const params = new URLSearchParams();
  if (filters?.object_type) params.append("object_type", filters.object_type);
  if (filters?.object_id) params.append("object_id", String(filters.object_id));
  if (filters?.file_id) params.append("file_id", String(filters.file_id));
  if (filters?.status) params.append("status", filters.status);
  if (filters?.role) params.append("role", filters.role);
  if (filters?.include_unavailable) params.append("include_unavailable", "true");

  const qs = params.toString();
  const url = qs
    ? `${endPoints.ProjectFileAttachments(projectId)}?${qs}`
    : endPoints.ProjectFileAttachments(projectId);

  const { data } = await axiosInstance.get<{ data: FileAttachmentListResponse; message: string; success: boolean }>(url);
  return data?.data;
};

export const useFileAttachments = (
  projectId: number | null,
  filters: FileAttachmentFilters = {},
  enabled = true,
) => {
  const filtersKey: FileAttachmentFilters = {
    object_type: filters.object_type || "",
    object_id: filters.object_id ?? 0,
    file_id: filters.file_id ?? 0,
    status: filters.status || "",
    role: filters.role || "",
    include_unavailable: filters.include_unavailable ?? false,
  };

  return useQuery({
    queryKey: queryKeys.ProjectFileAttachments(projectId, filtersKey),
    queryFn: fetchFileAttachments,
    enabled: !!projectId && enabled,
    staleTime: 15 * 1000,
  });
};

// ── Attach File ───────────────────────────────────────────────────────────────

const postAttachFile = async ({
  projectId,
  payload,
}: {
  projectId: number;
  payload: CreateAttachmentPayload;
}) => {
  const { data } = await axiosInstance.post<{ data: FileAttachment & { project_id: number; file_id: number; folder_id?: number }; message: string; success: boolean }>(
    endPoints.ProjectFileAttachments(projectId),
    payload,
  );
  return data?.data;
};

export const useAttachProjectFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postAttachFile,
    onSuccess: (result, variables) => {
      if (result) {
        invalidateAttachmentRelatedQueries({
          queryClient,
          projectId: variables.projectId,
          objectType: variables.payload?.object_type,
          fileId: result.file_id,
        });
      }
    },
  });
};

// ── Detach File Attachment ────────────────────────────────────────────────────

const deleteAttachment = async ({
  projectId,
  attachmentId,
}: {
  projectId: number;
  attachmentId: number;
}) => {
  const { data } = await axiosInstance.delete<{ data: { object_type: string; object_id: number; file_id?: number }; message: string; success: boolean }>(
    endPoints.ProjectFileAttachmentDetail(projectId, attachmentId),
  );
  return data?.data;
};

export const useDetachFileAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAttachment,
    onSuccess: (result, variables) => {
      if (result) {
        invalidateAttachmentRelatedQueries({
          queryClient,
          projectId: variables.projectId,
          objectType: result.object_type,
          fileId: result.file_id,
        });
      }
    },
  });
};

// ── Upload And Attach ─────────────────────────────────────────────────────────
// This is composed from existing usePresignUpload + useConfirmUpload but with
// attach_to payload. Use the confirm upload endpoint with attach_to.

const postConfirmWithAttach = async ({
  projectId,
  s3_key,
  original_name,
  content_type,
  size_bytes,
  folder_id,
  attach_to,
}: {
  projectId: number;
  s3_key: string;
  original_name: string;
  content_type: string;
  size_bytes: number;
  folder_id?: number | null;
  attach_to?: CreateAttachmentPayload;
}) => {
  const payload: Record<string, unknown> = {
    s3_key,
    original_name,
    content_type,
    size_bytes,
    folder_id: folder_id ?? null,
  };
  if (attach_to) {
    payload.attach_to = {
      object_type: attach_to.object_type,
      object_id: attach_to.object_id,
      role: attach_to.role || "CONTEXT",
      requirement_key: attach_to.requirement_key || "",
      note: attach_to.note || "",
    };
  }
  const { data } = await axiosInstance.post<{ data: { file: unknown; attachment?: FileAttachment }; message: string; success: boolean }>(
    endPoints.ProjectFilesConfirm(projectId),
    payload,
  );
  return data?.data;
};

export const useUploadAndAttachProjectFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postConfirmWithAttach,
    onSuccess: (result, variables) => {
      if (result) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.ProjectFilesBase(variables.projectId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.ProjectFolderTree(variables.projectId),
        });
        if (variables.attach_to) {
          invalidateAttachmentRelatedQueries({
            queryClient,
            projectId: variables.projectId,
            objectType: variables.attach_to.object_type,
            fileId: result.attachment?.file?.id,
          });
        }
      }
    },
  });
};
